type Json = unknown;
type Obj = Record<string, Json>;

const isObj = (v: Json): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);

export function deepEqual(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as Json[];
    return a.length === bb.length && a.every((x, i) => deepEqual(x, bb[i]));
  }
  const ka = Object.keys(a as Obj);
  const kb = Object.keys(b as Obj);
  return ka.length === kb.length && ka.every((k) => deepEqual((a as Obj)[k], (b as Obj)[k]));
}

/**
 * Three-way merge of one record: what both sides started from (`base`), this device's copy
 * (`mine`) and the share's copy (`theirs`).
 *  - a field only one side changed keeps that side's change;
 *  - a field both changed: objects are merged inside (so two different areas marked done on two
 *    devices both stay done); anything else keeps this device's value (it's the newer edit:
 *    it's being sent now).
 *  - deleted (null): a delete wins over an untouched copy; an edit wins over a delete.
 */
export function merge3(base: Json, mine: Json, theirs: Json): Json {
  if (deepEqual(mine, theirs)) return mine;
  if (deepEqual(mine, base)) return theirs;
  if (deepEqual(theirs, base)) return mine;
  if (isObj(mine) && isObj(theirs)) {
    const b = isObj(base) ? base : {};
    const out: Obj = {};
    for (const k of new Set([...Object.keys(mine), ...Object.keys(theirs)])) {
      const v = merge3(b[k], mine[k], theirs[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  if (mine === null || mine === undefined) return theirs ?? null;
  return mine;
}

/** A merged record gets a fresh version and time, so every device sees it as changed. */
export function mergeRecord(base: Obj | null | undefined, mine: Obj | null, theirs: Obj | null): Obj | null {
  const merged = merge3(base ?? null, mine, theirs) as Obj | null;
  if (!merged) return null;
  if (typeof merged.version === 'number') {
    merged.version = Math.max(Number(mine?.version ?? 0), Number(theirs?.version ?? 0)) + 1;
    merged.updatedAt = new Date().toISOString();
  }
  return merged;
}
